import { useState, useEffect, useMemo, MouseEvent } from "react";
import { Deal, DealStatus } from "../types";
import { currency, Card, Button } from "@repo/o37-ui";
import { LegacyToast as Toast, useToast } from "@repo/o37-ui";
import DealModal from "./DealModal";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

const PIPELINE_COLUMNS: { status: DealStatus; label: string; color: string; bgColor: string }[] = [
  { status: "qualified", label: "Qualified", color: "text-amber-600", bgColor: "bg-amber-50" },
  { status: "pre-negotiation", label: "Pre-Negotiation", color: "text-blue-600", bgColor: "bg-blue-50" },
  { status: "negotiation", label: "Negotiation", color: "text-purple-600", bgColor: "bg-purple-50" },
  { status: "closed", label: "Closed", color: "text-emerald-600", bgColor: "bg-emerald-50" },
];

export default function PartnerKanbanView({ partnerId }: { partnerId: string }) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedDeal, setDraggedDeal] = useState<Deal | null>(null);
  const [paidExpanded, setPaidExpanded] = useState(false);
  const [lostExpanded, setLostExpanded] = useState(false);
  const [showIdiotToast, setShowIdiotToast] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (partnerId) {
      loadDeals();
    }
  }, [partnerId]);

  const loadDeals = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/deals/partner/${partnerId}`);
      if (response.ok) {
        const data = await response.json();
        setDeals(data);
      }
    } catch (error) {
      console.error("Failed to load deals:", error);
    } finally {
      setLoading(false);
    }
  };

  const openDeal = async (dealId: string) => {
    try {
      setOpeningId(dealId);
      const response = await fetch(`${API_URL}/deals/${dealId}`);
      if (response.ok) {
        const deal = await response.json();
        setSelectedDeal(deal);
      } else {
        showToast("Failed to load deal details", "error");
      }
    } catch (error) {
      console.error("Failed to load deal:", error);
      showToast("Failed to load deal details", "error");
    } finally {
      setOpeningId(null);
    }
  };

  const updateDealStatus = (dealId: string, newStatus: DealStatus) => {
    const previousDeals = [...deals];
    setDeals(deals.map(d => 
      d.id === dealId ? { ...d, status: newStatus } : d
    ));
    setDraggedDeal(null);

    fetch(`${API_URL}/deals/${dealId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    }).then(response => {
      if (!response.ok) {
        setDeals(previousDeals);
        console.error("Failed to update deal status");
      }
    }).catch(error => {
      setDeals(previousDeals);
      console.error("Failed to update deal:", error);
    });
  };

  const groupedDeals = useMemo(() => {
    const groups: Record<string, Deal[]> = {
      qualified: [],
      "pre-negotiation": [],
      negotiation: [],
      closed: [],
      paid: [],
      lost: [],
    };
    deals.forEach(deal => {
      if (groups[deal.status]) {
        groups[deal.status].push(deal);
      }
    });
    return groups;
  }, [deals]);

  const handleDealClick = (deal: Deal) => {
    if (!isDragging) {
      openDeal(deal.id);
    }
  };

  const handleDragStart = (e: React.DragEvent, deal: Deal) => {
    setIsDragging(true);
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetStatus: DealStatus) => {
    e.preventDefault();
    if (draggedDeal && draggedDeal.status !== targetStatus) {
      updateDealStatus(draggedDeal.id, targetStatus);
    } else {
      setDraggedDeal(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedDeal(null);
    // Reset isDragging after a short delay to prevent click from firing
    setTimeout(() => setIsDragging(false), 100);
  };

  const getColumnTotal = (status: string) => {
    return (groupedDeals[status] || []).reduce((sum, d) => sum + d.dealValue, 0);
  };

  const pipelineDeals = deals.filter(d => !['paid', 'lost'].includes(d.status));

  if (loading) {
    return <div className="py-20 text-center text-neutral-500">Loading...</div>;
  }

  return (
    <div className="pb-20">
      {/* Idiot Toast */}
      {showIdiotToast && (
        <Toast 
          message="Idiot..." 
          duration={10000} 
          onClose={() => setShowIdiotToast(false)} 
        />
      )}

      {/* Deal Modal */}
      {selectedDeal && (
        <DealModal
          deal={selectedDeal}
          partnerId={partnerId}
          onClose={() => setSelectedDeal(null)}
          onSaved={loadDeals}
        />
      )}

      {/* Header */}
      <section className="px-6 sm:px-12 mt-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] uppercase tracking-wider text-neutral-500">Pipeline</h2>
            <div className="text-[12px] text-neutral-400">
              {pipelineDeals.length} active · {currency(pipelineDeals.reduce((sum, d) => sum + d.dealValue, 0))}
            </div>
          </div>
        </div>
      </section>

      {/* Final Status Drop Zones */}
      <section className="px-6 sm:px-12 mb-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 gap-3">
            {/* Lost Drop Zone */}
            <div
              className={`rounded-xl border transition-colors ${
                draggedDeal ? 'border-red-300 bg-red-50/50' : 'border-neutral-200 bg-neutral-50/50'
              }`}
              onDragOver={handleDragOver}
              onDrop={(e) => {
                if (draggedDeal && draggedDeal.status !== 'lost') {
                  setShowIdiotToast(true);
                }
                handleDrop(e, 'lost');
              }}
            >
              <Button
                variant="ghost"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  setLostExpanded(!lostExpanded);
                }}
                className="w-full px-4 py-3 flex items-center justify-between text-left h-auto hover:bg-transparent"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[11px] uppercase tracking-wider text-neutral-500">Lost</span>
                  <span className="text-[11px] text-neutral-400">
                    {groupedDeals.lost?.length || 0} deals · {currency(getColumnTotal('lost'))}
                  </span>
                </div>
                <span className="text-[10px] text-neutral-300">
                  {lostExpanded ? '−' : '+'}
                </span>
              </Button>
              
              {lostExpanded && (
                <div className="px-4 pb-3 border-t border-neutral-100">
                  {groupedDeals.lost?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {groupedDeals.lost.map(deal => (
                        <MiniDealCard
                          key={deal.id}
                          deal={deal}
                          isDragging={draggedDeal?.id === deal.id}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleDealClick(deal)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-[11px] text-neutral-300 italic mt-3">Drop deals here to mark as lost</div>
                  )}
                </div>
              )}
            </div>

            {/* Paid Drop Zone */}
            <div
              className={`rounded-xl border transition-colors ${
                draggedDeal ? 'border-emerald-300 bg-emerald-50/50' : 'border-neutral-200 bg-neutral-50/50'
              }`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'paid')}
            >
              <Button
                variant="ghost"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  setPaidExpanded(!paidExpanded);
                }}
                className="w-full px-4 py-3 h-auto hover:bg-transparent"
                text={
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] uppercase tracking-wider text-neutral-500">Paid</span>
                      <span className="text-[11px] text-neutral-400">
                        {groupedDeals.paid?.length || 0} deals · {currency(getColumnTotal('paid'))}
                      </span>
                    </div>
                    <span className="text-[10px] text-neutral-300">
                      {paidExpanded ? '−' : '+'}
                    </span>
                  </div>
                }
              />
              
              {paidExpanded && (
                <div className="px-4 pb-3 border-t border-neutral-100">
                  {groupedDeals.paid?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {groupedDeals.paid.map(deal => (
                        <MiniDealCard
                          key={deal.id}
                          deal={deal}
                          isDragging={draggedDeal?.id === deal.id}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleDealClick(deal)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-[11px] text-neutral-300 italic mt-3">Drop deals here to mark as paid</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Kanban Board */}
      <section className="px-6 sm:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-4 gap-3">
            {PIPELINE_COLUMNS.map(column => (
              <div
                key={column.status}
                className="flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.status)}
              >
                {/* Column Header */}
                <div className={`rounded-t-xl px-3 py-2.5 border border-b-0 border-neutral-200 ${column.bgColor}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] uppercase tracking-wider font-medium ${column.color}`}>
                      {column.label}
                    </span>
                    <span className="text-[10px] text-neutral-400">
                      {groupedDeals[column.status]?.length || 0}
                    </span>
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    {currency(getColumnTotal(column.status))}
                  </div>
                </div>

                {/* Column Body */}
                <div className="flex-1 bg-neutral-50/50 rounded-b-xl border border-neutral-200 min-h-[320px] p-2 space-y-2">
                  {(!groupedDeals[column.status] || groupedDeals[column.status].length === 0) ? (
                    <div className="h-14 flex items-center justify-center text-neutral-300 text-[11px] border border-dashed border-neutral-200 rounded-lg">
                      Drop here
                    </div>
                  ) : (
                    groupedDeals[column.status].map(deal => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        isDragging={draggedDeal?.id === deal.id}
                        isOpening={openingId === deal.id}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleDealClick(deal)}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function DealCard({ 
  deal, 
  isDragging, 
  isOpening,
  onDragStart, 
  onDragEnd,
  onClick
}: { 
  deal: Deal; 
  isDragging: boolean;
  isOpening: boolean;
  onDragStart: (e: React.DragEvent, deal: Deal) => void;
  onDragEnd: () => void;
  onClick: () => void;
}) {
  return (
    <Card
      className={`p-3 cursor-grab active:cursor-grabbing transition-all duration-100 hover:shadow-sm ${
        isDragging ? "opacity-40 scale-[0.98]" : ""
      } ${isOpening ? "opacity-70" : ""}`}
      draggable
      onDragStart={(e: React.DragEvent<HTMLDivElement>) => onDragStart(e, deal)}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      <div className="text-xs font-medium text-neutral-900 truncate">{deal.name}</div>
      
      {deal.company && (
        <div className="text-[11px] text-neutral-500 truncate mt-0.5">{deal.company}</div>
      )}

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-neutral-100">
        <span className="text-xs font-semibold text-neutral-900">{currency(deal.dealValue)}</span>
        {deal.closeProbability !== undefined && deal.closeProbability > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-10 h-1 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  deal.closeProbability > 75 ? "bg-emerald-400" :
                  deal.closeProbability > 40 ? "bg-amber-400" : "bg-neutral-300"
                }`}
                style={{ width: `${deal.closeProbability}%` }}
              />
            </div>
            <span className="text-[10px] text-neutral-400">{deal.closeProbability}%</span>
          </div>
        )}
      </div>
    </Card>
  );
}

function MiniDealCard({ 
  deal,
  isDragging,
  onDragStart,
  onDragEnd,
  onClick
}: { 
  deal: Deal;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, deal: Deal) => void;
  onDragEnd: () => void;
  onClick: () => void;
}) {
  return (
    <div 
      className={`bg-white rounded-lg border border-neutral-100 p-2 cursor-grab active:cursor-grabbing transition-all duration-100 hover:shadow-sm ${
        isDragging ? "opacity-40 scale-[0.98]" : ""
      }`}
      draggable
      onDragStart={(e: React.DragEvent) => onDragStart(e, deal)}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      <div className="text-xs font-medium text-neutral-900 truncate">{deal.name}</div>
      <div className="text-[11px] text-neutral-500">{currency(deal.dealValue)}</div>
    </div>
  );
}
